import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type FormEvent } from "react";
import { formatText } from "../i18n/texts";
import { registerLeaveGuard } from "../shell/leaveGuard";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { FieldError, SectionMessage } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { confirmDiscard, resolveDiscard, TaskWindow } from "../ui/TaskWindow";
import { ContactRequestFailure } from "./contactDirectoryClient";
import { fullName, type ContactPerson, type ContactRights } from "./contactModel";
import { ContactTagSelector } from "./ContactTagSelector";
import type { ContactWorkspaceController, EditorHandle } from "./contactWorkspaceController";
import { focusField } from "./workspaceDom";

// Crear y editar un contacto (ContactEditor.razor del portal actual): mismo formulario, mismas validaciones antes de
// enviar, aviso de posibles duplicados (hay que confirmar con «Crear de todos modos») y la versión esperada para no
// pisar cambios ajenos. Salir con cambios pide confirmar. El servidor vuelve a validar todo.

type Draft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationshipStatus: string;
  tags: string[];
};

const fromContact = (contact: ContactPerson | null | undefined): Draft => ({
  firstName: contact?.firstName ?? "",
  lastName: contact?.lastName ?? "",
  email: contact?.email ?? "",
  phone: contact?.phone ?? "",
  relationshipStatus: contact?.relationshipStatus ?? "",
  tags: [...(contact?.tags ?? [])],
});
const values = (draft: Draft) =>
  JSON.stringify([draft.firstName, draft.lastName, draft.email, draft.phone, draft.relationshipStatus, draft.tags]);
const control = /[\u0000-\u001f\u007f-\u009f]/;
const newKey = () => crypto.randomUUID().replace(/-/g, "");

/** Correo como lo acepta MailAddress de .NET sin nombre visible: local@dominio, sin espacios ni separadores. */
const validEmail = (value: string) => /^[^\s@<>()",;:\\[\]]+@[^\s@<>()",;:\\[\]]+$/.test(value) && !value.endsWith(".");

export const ContactEditor = forwardRef<
  EditorHandle,
  { ctl: ContactWorkspaceController; contact: ContactPerson | null; rights: ContactRights | null | undefined }
>(function ContactEditor({ ctl, contact, rights }, ref) {
  const [draft, setDraft] = useState<Draft>(() => fromContact(contact));
  const [original, setOriginal] = useState(() => values(fromContact(contact)));
  const [version, setVersion] = useState<number | null>(contact?.rowVersion ?? null);
  const [key, setKey] = useState(newKey);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<ContactPerson | null>(null);
  const [matches, setMatches] = useState<ContactPerson[]>([]);
  const [reviewed, setReviewed] = useState<{ values: string; ids: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [focusErrors, setFocusErrors] = useState<string | null>(null);
  const [scopeRevision, setScopeRevision] = useState(ctl.scopeRevision);
  const discardWindow = useRef<HTMLDialogElement>(null);
  const lifetime = useRef(new AbortController());
  const dirty = !allowNavigation && values(draft) !== original;
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  });
  // Otro alcance invalida los avisos de duplicados ya revisados.
  if (scopeRevision !== ctl.scopeRevision) {
    setScopeRevision(ctl.scopeRevision);
    setMatches([]);
    setReviewed(null);
    setCurrent(null);
  }
  useEffect(() => {
    if (lifetime.current.signal.aborted) lifetime.current = new AbortController();
    const controller = lifetime.current;
    // ConfirmExternalNavigation: salir de la página con cambios pide confirmar al navegador.
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) event.preventDefault();
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      controller.abort();
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, []);
  useEffect(() => {
    if (focusErrors) {
      focusField("c1-edit-" + focusErrors);
      setFocusErrors(null);
    }
  }, [focusErrors]);
  const confirmLeave = async () => {
    if (dirtyRef.current && !(await confirmDiscard(discardWindow.current!))) return false;
    setAllowNavigation(true);
    dirtyRef.current = false;
    return true;
  };
  const confirmLeaveRef = useRef(confirmLeave);
  useEffect(() => {
    confirmLeaveRef.current = confirmLeave;
  });
  useImperativeHandle(ref, () => ({ confirmLeave }));
  // OnBeforeInternalNavigation (GuardAsync): los enlaces del shell a otra pantalla migrada también preguntan.
  useEffect(() => registerLeaveGuard(() => confirmLeaveRef.current()), []);

  // La ventana de descartar existe siempre, también sin permiso (como en el portal actual).
  const discardTask = (
    <TaskWindow ref={discardWindow} className="c1-dialog" titleId="c1-discard-title">
      <h2 id="c1-discard-title">{ctl.L("UNSAVED")}</h2>
      <div className="actions c1-window-actions">
        <button
          type="button"
          className="button secondary"
          onClick={() => resolveDiscard(discardWindow.current!, false)}
        >
          {ctl.L("KEEP_EDITING")}
        </button>
        <button type="button" className="button" onClick={() => resolveDiscard(discardWindow.current!, true)}>
          {ctl.L("DISCARD")}
        </button>
      </div>
    </TaskWindow>
  );
  if (rights?.canWrite !== true || (contact === null && rights.globalScope !== true))
    return (
      <>
        <div className="c1-editor" data-dirty="false">
          <SectionMessage message={ctl.L("DENIED")} tone="error" />
        </div>
        {discardTask}
      </>
    );

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const reloadCurrent = () => {
    const next = fromContact(current);
    setDraft(next);
    setOriginal(values(next));
    setVersion(current?.rowVersion ?? null);
    setCurrent(null);
    setIssues({});
    setError(null);
    setKey(newKey());
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const found: Record<string, string> = {};
    setError(null);
    const badName = (value: string) => !value.trim() || [...value.trim()].length > 100 || control.test(value);
    if (badName(draft.firstName)) found.FirstName = ctl.L(draft.firstName.trim() ? "NAME_ERROR" : "FIRST_REQUIRED");
    if (badName(draft.lastName)) found.LastName = ctl.L(draft.lastName.trim() ? "NAME_ERROR" : "LAST_REQUIRED");
    if (draft.email.length > 0 && (draft.email.length > 200 || !validEmail(draft.email.trim())))
      found.Email = ctl.L("EMAIL_ERROR");
    const phone = draft.phone.trim();
    const digits = [...phone].filter((char) => char >= "0" && char <= "9");
    if (
      phone.length > 0 &&
      (phone.length > 50 ||
        !phone.startsWith("+") ||
        [...phone].some((char) => !(char >= "0" && char <= "9") && !"+ -()".includes(char)) ||
        phone.slice(1).includes("+") ||
        digits.length < 7 ||
        digits.length > 15 ||
        digits[0] === "0")
    )
      found.Phone = ctl.L("PHONE_ERROR");
    if (draft.tags.length > 20 || draft.tags.some((tag) => tag.length > 50 || control.test(tag)))
      found.Tags = ctl.L("TAGS_ERROR");
    setIssues(found);
    if (Object.keys(found).length > 0) {
      setFocusErrors(Object.keys(found)[0]!);
      return;
    }
    setSaving(true);
    try {
      const details = {
        email: draft.email,
        phone: draft.phone,
        relationshipStatus: draft.relationshipStatus,
        tags: draft.tags,
      };
      const duplicates = await ctl.api.matches(details, contact?.id ?? null, lifetime.current.signal);
      const ids = duplicates.map((item) => item.id).join(",");
      if (duplicates.length > 0 && (reviewed === null || reviewed.values !== values(draft) || reviewed.ids !== ids)) {
        setMatches(duplicates);
        setReviewed({ values: values(draft), ids });
        return;
      }
      const result = await ctl.api.save(
        contact?.id ?? null,
        { firstName: draft.firstName, lastName: draft.lastName, expectedVersion: version, commercial: details },
        key,
        lifetime.current.signal,
      );
      if (lifetime.current.signal.aborted) return;
      setAllowNavigation(true);
      dirtyRef.current = false;
      await ctl.saved(result.contact.id);
    } catch (exception) {
      if (lifetime.current.signal.aborted) return;
      setError(ctl.failure(exception));
      if (exception instanceof ContactRequestFailure && exception.code === "STALE_VERSION" && contact) {
        try {
          setCurrent((await ctl.api.get(contact.id)).contact);
        } catch {
          setCurrent(null);
        }
      }
    } finally {
      setSaving(false);
    }
  };
  const relationshipOptions = [
    { value: "", label: ctl.L("UNDEFINED") },
    ...["PROSPECT", "CUSTOMER", "PARTNER", "OTHER"].map((value) => ({ value, label: ctl.L(value) })),
  ];
  const invalid = (name: string) => (name in issues ? "true" : "false");
  return (
    <>
      <div className="c1-editor" data-dirty={dirty ? "true" : "false"}>
        {error !== null ? <SectionMessage message={error} tone="error" /> : null}
        {current !== null ? (
          <>
            <p>
              {ctl.L("CURRENT")}: {fullName(current)}
            </p>
            <button className="button secondary" onClick={reloadCurrent}>
              {ctl.L("RELOAD")}
            </button>
          </>
        ) : null}
        {matches.length > 0 ? (
          <>
            <SectionMessage message={ctl.L("MATCH_WARNING")} tone="warning" />
            <ul>
              {matches.map((match) => (
                <li key={match.id}>
                  <a href={ctl.listUrl(match.id)}>{fullName(match)}</a>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <form id="c1-contact-form" onSubmit={(event) => void save(event)}>
          <div className="c1-form-grid">
            <label className="field" htmlFor="c1-edit-FirstName">
              <span className="c1-field-label">{ctl.field("FirstName")}</span>
              <input
                id="c1-edit-FirstName"
                value={draft.firstName}
                onChange={(event) => set({ firstName: event.target.value })}
                autoComplete="given-name"
                aria-describedby="c1-error-FirstName"
                aria-invalid={invalid("FirstName")}
              />
              <FieldError id="c1-error-FirstName" message={issues.FirstName} />
            </label>
            <label className="field" htmlFor="c1-edit-LastName">
              <span className="c1-field-label">{ctl.field("LastName")}</span>
              <input
                id="c1-edit-LastName"
                value={draft.lastName}
                onChange={(event) => set({ lastName: event.target.value })}
                autoComplete="family-name"
                aria-describedby="c1-error-LastName"
                aria-invalid={invalid("LastName")}
              />
              <FieldError id="c1-error-LastName" message={issues.LastName} />
            </label>
            <label className="field" htmlFor="c1-edit-Phone">
              <span className="c1-field-label">
                {ctl.field("Phone")} <span className="c1-optional">{ctl.L("OPTIONAL")}</span>
              </span>
              <span className="c1-phone-field c1-phone-prefix">
                <strong aria-hidden="true">+</strong>
                <input
                  className="c1-phone-input"
                  id="c1-edit-Phone"
                  type="tel"
                  value={draft.phone.replace(/^\++/, "")}
                  onChange={(event) =>
                    set({ phone: event.target.value.trim() ? "+" + event.target.value.replace(/^\++/, "") : "" })
                  }
                  autoComplete="tel"
                  readOnly={contact?.phoneVerified === true}
                  aria-describedby="c1-phone-help c1-error-Phone"
                  aria-invalid={invalid("Phone")}
                />
              </span>
              <span id="c1-phone-help">
                {contact?.phoneVerified === true ? ctl.L("VERIFIED") : ctl.L("PHONE_HELP")}
              </span>
              <FieldError id="c1-error-Phone" message={issues.Phone} />
            </label>
            <label className="field" htmlFor="c1-edit-Email">
              <span className="c1-field-label">
                {ctl.field("Email")} <span className="c1-optional">{ctl.L("OPTIONAL")}</span>
              </span>
              <input
                id="c1-edit-Email"
                type="email"
                value={draft.email}
                onChange={(event) => set({ email: event.target.value })}
                autoComplete="email"
                readOnly={contact?.emailVerified === true}
                aria-describedby="c1-error-Email"
                aria-invalid={invalid("Email")}
              />
              {contact?.emailVerified === true ? <span>{ctl.L("VERIFIED")}</span> : null}
              <FieldError id="c1-error-Email" message={issues.Email} />
            </label>
          </div>
          <details className="c1-disclosure" open={"Tags" in issues ? true : undefined}>
            <summary>
              <ChatIcon name="chevron-down" />
              {ctl.L("SECONDARY")}
              <span className="c1-meta">
                {" "}
                · {draft.relationshipStatus ? ctl.L(draft.relationshipStatus) : ctl.L("UNDEFINED")} ·{" "}
                {formatText(ctl.L(draft.tags.length === 1 ? "TAG_COUNT_ONE" : "TAG_COUNT"), draft.tags.length)}
              </span>
            </summary>
            <div className="c1-form-grid">
              <div className="field">
                <span className="c1-field-label">{ctl.field("RelationshipStatus")}</span>
                <ChoiceMenu
                  label={ctl.field("RelationshipStatus")}
                  value={draft.relationshipStatus}
                  onChange={(value) => set({ relationshipStatus: value })}
                  options={relationshipOptions}
                  chevron={<ChatIcon name="chevron-down" />}
                  check={<ChatIcon name="check" />}
                />
              </div>
              <div className="field" id="c1-edit-Tags">
                <span className="c1-field-label">{ctl.field("Tags")}</span>
                <ContactTagSelector
                  api={ctl.api}
                  labels={ctl.labels}
                  tenant={ctl.tenant}
                  user={ctl.user}
                  contactId={contact?.id ?? null}
                  value={draft.tags}
                  draftOnly
                  colorOf={ctl.tagColor}
                  onChanged={(tags) => setDraft((previous) => ({ ...previous, tags }))}
                />
                <FieldError id="c1-error-Tags" message={issues.Tags} />
              </div>
            </div>
          </details>
          <div className="actions c1-editor-actions">
            <a className="button secondary" href={ctl.listUrl(contact?.id ?? null)}>
              {ctl.L("CANCEL")}
            </a>
            <button className="button" type="submit" disabled={saving} aria-busy={saving ? "true" : "false"}>
              {matches.length > 0 && contact === null ? ctl.L("CREATE_DISTINCT") : ctl.L("SAVE")}
            </button>
          </div>
        </form>
      </div>
      {discardTask}
    </>
  );
});
